// Read-only subset of the Data360 frontend's services/api.js for the Risk
// Framework tab. All calls go to the Data360 backend, so this tab and the
// Data360 app read the same database-backed thresholds. No write endpoints.
import { Data360AxiosInstance as apiClient } from "../../../../services/Data360Axios";

const cleanParams = (params) =>
  Object.entries(params || {})
    .filter(([, value]) => {
      if (value === null || value === undefined || value === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

export const workflowStateApi = {
  getDates: async () => {
    const response = await apiClient.get("/workflow/dates");
    return response.data;
  },
};

export const snaSooApi = {
  getNavByDate: async (date) => {
    const response = await apiClient.get("/sna-soo/nav-by-date", { params: { date } });
    return response.data?.data;
  },
};

export const riskLimitsApi = {
  // Live exposure (data360-computed) vs. admin-configured limit thresholds,
  // joined server-side by strategy type.
  getUtilization: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/utilization", cleanParams(params));
    return response.data;
  },

  // Drill-down: exposure for one strategy type, broken out by strategy and investment code.
  getUtilizationDetail: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/utilization/detail", cleanParams(params));
    return response.data;
  },

  // Issuer-level drill-down: exposure for one investment code, broken out by issuer.
  getIssuerDetail: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/utilization/detail/issuers", cleanParams(params));
    return response.data;
  },

  // Firmwide VaR 95% / 99% (Risk Monitor); returns { valuation_date, var_95, var_99, source }.
  getFirmwideVar: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/var/firmwide", cleanParams(params));
    return response.data?.data;
  },

  // VaR 95% / 99% per strategy within one strategy type (Risk Monitor Monte
  // Carlo VaR); response.valuation_date is the simulation date used.
  getStrategyVar: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/var/strategies", cleanParams(params));
    return response.data;
  },

  // VaR 95% / 99% per investment code within one strategy type (each
  // investment's P&L in the type's VaR scenario, so rows can be summed).
  getInvestmentVar: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/var/investments", cleanParams(params));
    return response.data;
  },

  // Portfolio greeks shock table (Delta, DV01, etc.) from Risk Monitor.
  getPortfolioGreeks: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/portfolio-greeks", cleanParams(params));
    return response.data?.data;
  },

  // Merger Arbitrage deal break risk per strategy: { valuation_date, data: [{ strategy_name, deal_break_risk, position_count }] }.
  getDealBreakRisk: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/deal-break-risk", cleanParams(params));
    return response.data?.data;
  },

  // SPAC Risk tables: { valuation_date, common, warrant, right } each [{ symbol, current_price, lmv, risk }].
  getSpacRisk: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/spac-risk", cleanParams(params));
    return response.data?.data;
  },

  // Daily LMV/SMV + VaR 95%/99% history, firmwide and per strategy type (Firmwide
  // Summary's Strategy Risk Analytics): { series: { <name>: [{ date, lmv, smv, var_95, var_99 }] }, var_available }.
  getRiskHistory: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/history", cleanParams(params));
    return response.data?.data;
  },

  // Daily LMV/SMV and net P&L per strategy_name within one strategy type (database only, fast):
  // { strategy_type_name, dates, strategies: { <name>: [{ date, lmv, smv, net_pnl }] } }.
  getStrategyRiskHistory: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/history/strategies", cleanParams(params));
    return response.data?.data;
  },

  // Daily VaR 95%/99% per strategy_name within one strategy type (Risk Monitor). kind: "standalone" (the strategy's
  // own VaR) or "contribution" (its P&L in the type's VaR scenario). Returns { dates, strategies: { <name>:
  // [{ date, var_95, var_99 }] }, pending, var_available }; pending > 0 means more days are still loading server-side.
  getStrategyVarHistory: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/history/strategies/var", cleanParams(params));
    return response.data?.data;
  },

  // Daily greeks shock P&L (DV01, CS01, Delta/Beta, Vega) per strategy_name within one strategy type (Risk Monitor):
  // { scenarios, dates, strategies: { <name>: [{ date, greeks: { <scenario>: pnl } | null }] }, greeks_available }.
  getStrategyGreeksHistory: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/history/strategies/greeks", cleanParams(params));
    return response.data?.data;
  },

  // Greeks P&L per strategy name and investment code within one strategy type
  // (Risk Monitor): { valuation_date, scenarios, strategies, investments }.
  getGreeksDetail: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/portfolio-greeks/detail", cleanParams(params));
    return response.data?.data;
  },

  // SPAC PM approval: the day's AUM, Allocated SPAC Risk Capital, every level
  // (static ratio x AUM / risk capital) and the selected PM approval level.
  getPmApproval: async (params = {}) => {
    const response = await apiClient.post("/risk-limits/pm-approval", cleanParams(params));
    return response.data?.data;
  },

  // Threshold Admin values are stored by date: reads return the snapshot in force on `date` (default today).
  getPmApprovalConfig: async (date) => {
    const response = await apiClient.get("/risk-limits/pm-approval/config", { params: date ? { date } : undefined });
    return response.data?.data;
  },

  getThresholds: async (date) => {
    const response = await apiClient.get("/risk-limits/thresholds", { params: date ? { date } : undefined });
    return response.data;
  },

};

export default apiClient;
